import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { ChevronsUpDown, Check, X } from 'lucide-react';

/**
 * Popover + searchable Command list where multiple items can be picked.
 * Selected values render as chips above the trigger, each with an X to
 * remove. Designed for cases where the option list is long enough to
 * need search (regions, states, connected users, etc.) but short
 * enough to fit in a single popover column.
 *
 * Props:
 *   options      — [{ value, label }] — the pickable items
 *   value        — string[] — currently selected values
 *   onChange(next) — called with the new value[] whenever it changes
 *   placeholder  — text on the trigger when nothing is picked
 *   searchPlaceholder — CommandInput placeholder
 *   emptyText    — shown when the search filter matches nothing
 *   disabled     — dim the trigger and stop opening
 *   testId       — optional data-testid prefix (`${testId}-trigger`, etc.)
 */
export default function MultiSelectChips({
  options,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyText,
  disabled = false,
  testId,
}) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const selectedSet = new Set(value || []);

  const toggle = (val) => {
    if (selectedSet.has(val)) {
      onChange((value || []).filter((v) => v !== val));
    } else {
      onChange([...(value || []), val]);
    }
  };

  const remove = (val) => {
    onChange((value || []).filter((v) => v !== val));
  };

  const labelFor = (val) => options.find((o) => o.value === val)?.label ?? val;

  return (
    <div className="space-y-2">
      {value && value.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={testId ? `${testId}-chips` : undefined}>
          {value.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 rounded-full bg-farm-primary/10 text-farm-primary text-xs font-medium pl-2.5 pr-1 py-1"
              data-testid={testId ? `${testId}-chip-${v}` : undefined}
            >
              {labelFor(v)}
              <button
                type="button"
                onClick={() => remove(v)}
                disabled={disabled}
                className="rounded-full hover:bg-farm-primary/20 p-0.5"
                aria-label={t('common.remove', 'Remove')}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="w-full justify-between font-normal h-14 bg-white border-gray-200 text-base"
            data-testid={testId ? `${testId}-trigger` : undefined}
          >
            <span className={value && value.length > 0 ? '' : 'text-muted-foreground'}>
              {value && value.length > 0
                ? t('common.multiSelect.countSelected', {
                    count: value.length,
                    defaultValue: '{{count}} selected',
                  })
                : placeholder}
            </span>
            <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0 ml-2" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
          <Command>
            <CommandInput placeholder={searchPlaceholder} />
            <CommandList>
              <CommandEmpty>{emptyText}</CommandEmpty>
              <CommandGroup>
                {options.map((opt) => {
                  const picked = selectedSet.has(opt.value);
                  return (
                    <CommandItem
                      key={opt.value}
                      value={opt.label}
                      onSelect={() => toggle(opt.value)}
                      data-testid={testId ? `${testId}-option-${opt.value}` : undefined}
                    >
                      <Check className={`w-4 h-4 mr-2 ${picked ? 'opacity-100' : 'opacity-0'}`} />
                      {opt.label}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
